import { ContentCopyRounded, DeleteRounded, ExpandMore, SearchRounded } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Grid, IconButton, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRecoilState, useRecoilValue } from "recoil";
import { editAccordionExpandedState, editIsPersonBAState } from "../../../recoil";
import { FirstInvoiceRecipient, ICustomer, ReactQueryKey, RentalType } from "../../../../../types";
import { getCountries, getLegalEntities, getMobilePrefixes, IGetCompanyReturn, IGetCustomersParams } from "../../../../../api/services";
import { IFormValues } from "../../../types";
import { FormAutocomplete, FormDatePicker, FormTextField } from "../../../../../components/fields";
import useDialog from "../../../../../hooks/useDialog";
import DialogSearchCustomer from "../../../../../components/DialogSearchCustomer";

const LegalFormField = () => {

    const { t } = useTranslation('booking');

    const legalFormQuery = useQuery({
        queryKey: [ReactQueryKey.LegalForm],
        queryFn: getLegalEntities,
        refetchOnMount: false
    });

    return (
        <FormAutocomplete<IFormValues>
            name="bookingOwner.legalEntity"
            label={t('label.legalForm')}
            options={legalFormQuery.data ?? []}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            getOptionLabel={(option) => { return !!option.label ?  t(`legalForms.${option.label}`) : ''}}
            disabled={true}
        />
    )
}

const MobilePrefixField = () => {

    const { t } = useTranslation('booking');

    const mobilePrefixesQuery = useQuery({
        queryKey: [ReactQueryKey.MobilePrefixes],
        queryFn: getMobilePrefixes
    });

    return (
        <FormAutocomplete<IFormValues>
            name="bookingOwner.mobilePrefix"
            label={t('label.prefix')}
            options={mobilePrefixesQuery.data ?? []}
            isOptionEqualToValue={(option, value) => option.country === value.country}
            getOptionLabel={(option) => ('+' + option.prefix + ` (${option.country})`)}
            disabled
        />
    )
}

const BirthNationField = () => {

    const { t } = useTranslation('booking');

    const countriesQuery = useQuery({
        queryKey: [ReactQueryKey.Countries],
        queryFn: getCountries,
        refetchOnMount: false
    });

    return (
        <FormAutocomplete<IFormValues>
            name="bookingOwner.birthNation"
            label={t('label.countryOfBirth')}
            options={countriesQuery.data ?? []}
            disabled={true}
        />
    );
}

const ResidenceCountryField = () => {

    const { t } = useTranslation('booking');

    const countriesQuery = useQuery({
        queryKey: [ReactQueryKey.Countries],
        queryFn: getCountries,
        refetchOnMount: false
    });

    return (
        <FormAutocomplete<IFormValues>
            name="bookingOwner.residenceCountry"
            label={t('label.countryOfResidence')}
            options={countriesQuery.data ?? []}
            disabled={true}
        />
    );
}

const BookingOwnerDetails = () => {

    const { t } = useTranslation('booking');

    const [bookingOwnerExpanded, setBookingOwnerExpanded] = useRecoilState(editAccordionExpandedState("bookingOwner"));

    const formContext = useFormContext<IFormValues>();

    const rentalType = useWatch<IFormValues, 'basicDetails.rentalType'>({
        name: 'basicDetails.rentalType'
    });

    const isPersonBA = useRecoilValue(editIsPersonBAState);

    const searchDialog = useDialog();

    const [bookingOwnerFilter, setBookingOwnerFilter] = useState<IGetCustomersParams['filters'] | null>(null);

    const handleCopyClick = useCallback((e: React.SyntheticEvent) => {
        e.stopPropagation();
        const firstDriver = formContext.getValues('firstDriver');
        formContext.setValue("bookingOwner.id", firstDriver.id);
        formContext.setValue("bookingOwner.uuid", firstDriver.uuid);
        formContext.setValue("bookingOwner.name", firstDriver.name);
        formContext.setValue("bookingOwner.surname", firstDriver.surname);
        formContext.setValue("bookingOwner.birthDate", firstDriver.birthDate);
        formContext.setValue("bookingOwner.birthNation", firstDriver.birthNation);
        formContext.setValue("bookingOwner.birthPlace", firstDriver.birthPlace);
        formContext.setValue("bookingOwner.residenceCountry", firstDriver.residenceCountry);
        formContext.setValue("bookingOwner.fiscalCode", firstDriver.fiscalCode);
        formContext.setValue("bookingOwner.drivingLicenseNumber", firstDriver.drivingLicenseNumber);
        formContext.setValue("bookingOwner.email", firstDriver.email);
        formContext.setValue("bookingOwner.mobile", firstDriver.mobile);
    }, []);
    const handleSearchClick = useCallback((e: React.SyntheticEvent) => {
        e.stopPropagation();
        const bookingOwner = formContext.getValues('bookingOwner');
        setBookingOwnerFilter({
            firstName: bookingOwner.name,
            lastName: bookingOwner.surname,
            birthDate: bookingOwner.birthDate?.format('YYYY-MM-DD') || '',
            birthNation: bookingOwner.birthNation,
            birthPlace: bookingOwner.birthPlace,
            residenceCountry: bookingOwner.residenceCountry,
            fiscalCode: bookingOwner.fiscalCode,
            drivingLicenseNumber: bookingOwner.drivingLicenseNumber,
            mobile: bookingOwner.mobile,
            email: bookingOwner.email
        });
        searchDialog.handleShow();
    }, []);
    const handleCustomerSelected = useCallback((selectedCustomer: ICustomer) => {
        formContext.setValue('bookingOwner.name', selectedCustomer.firstName);
        formContext.setValue('bookingOwner.surname', selectedCustomer.lastName);
        formContext.setValue('bookingOwner.birthDate', selectedCustomer.birthDate !== '' ? dayjs(selectedCustomer.birthDate, 'YYYY-MM-DD') : null);
        formContext.setValue('bookingOwner.birthNation', selectedCustomer.birthNation);
        formContext.setValue('bookingOwner.birthPlace', selectedCustomer.birthPlace);
        formContext.setValue('bookingOwner.residenceCountry', selectedCustomer.residenceCountry);
        formContext.setValue('bookingOwner.fiscalCode', selectedCustomer.fiscalCode);
        formContext.setValue('bookingOwner.drivingLicenseNumber', selectedCustomer.drivingLicenseNumber);
        formContext.setValue('bookingOwner.mobile', selectedCustomer.mobile);
        formContext.setValue('bookingOwner.email', selectedCustomer.email);
    }, []);
    const handleDeleteClick = useCallback((e: React.SyntheticEvent) => {
        e.stopPropagation();
        formContext.resetField('bookingOwner')
    }, []);

    return (
        <>
            <DialogSearchCustomer
                section={'bookingOwner'}
                open={searchDialog.open}
                onClose={searchDialog.handleClose}
                onCustomerSelected={handleCustomerSelected}
                customersFilter={bookingOwnerFilter}
            />
            <Accordion expanded={bookingOwnerExpanded} onChange={(e, expanded) => setBookingOwnerExpanded(expanded)}>
                <AccordionSummary expandIcon={<ExpandMore />} sx={{
                    ".MuiAccordionSummary-content": {
                        justifyContent: "space-between",
                        alignItems: "center"
                    }
                }} >
                    <Typography variant="h5">{t('label.bookingOwner')}</Typography>
                    <Stack direction="row" mr={2}>
                        <IconButton onClick={handleCopyClick} disabled={true}>
                            <ContentCopyRounded fontSize="small" />
                        </IconButton>
                        <IconButton onClick={handleSearchClick} disabled={true} >
                            <SearchRounded fontSize="small" />
                        </IconButton>
                        <IconButton onClick={handleDeleteClick} disabled={true}>
                            <DeleteRounded fontSize="small" />
                        </IconButton>
                    </Stack>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container columnSpacing={3} rowSpacing={1}>
                        {(rentalType === RentalType.RETAIL || isPersonBA) &&
                            <>
                                <Grid item xs={12} sm={6}>
                                    <FormTextField
                                        name="bookingOwner.name"
                                        label={t('label.firstName')}
                                        disabled={true}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormTextField<IFormValues>
                                        name="bookingOwner.surname"
                                        label={t('label.lastName')}
                                        disabled={true}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormDatePicker<IFormValues>
                                        name="bookingOwner.birthDate"
                                        label={t('label.dateOfBirth')}
                                        disabled={true}
                                    />
                                </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                    name="bookingOwner.birthPlace"
                                    label={t('label.cityOfBirth')}
                                    disabled={true}
                                />
                            </Grid>
                                <Grid item xs={12} sm={6}>
                                    <BirthNationField />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <ResidenceCountryField />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormTextField<IFormValues>
                                        name="bookingOwner.fiscalCode"
                                        label={t('label.fiscalCode')}
                                        disabled={true}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormTextField<IFormValues>
                                        name="bookingOwner.drivingLicenseNumber"
                                        label={t('label.drivingLicenseNumber')}
                                        disabled={true}
                                    />
                                </Grid>
                            </>
                        }
                        {(rentalType === RentalType.CORPORATE && !isPersonBA) &&
                        <>
                            <Grid item xs={12} sm={6}>
                                <LegalFormField />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                    name="bookingOwner.companyName"
                                    label={t('label.companyName')}
                                    disabled={true}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                    name="bookingOwner.country"
                                    label={t('label.country')}
                                    disabled={true}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                    name="bookingOwner.city"
                                    label={t('label.city')}
                                    disabled={true}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                    name="bookingOwner.streetName"
                                    label={t('label.streetName')}
                                    disabled={true}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                    name="bookingOwner.streetNumber"
                                    label={t('label.streetNumber')}
                                    disabled={true}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                    name="bookingOwner.postalCode"
                                    label={t('label.postalCode')}
                                    disabled={true}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                    name="bookingOwner.certifiedEmail"
                                    label={t('label.certifiedEmailAddress')}
                                    disabled={true}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                    name="bookingOwner.telephone"
                                    label={t('label.telephone')}
                                    disabled={true}
                                />
                            </Grid>
                            {!formContext.getValues('bookingOwner.publicAuthority') &&
                                <Grid item xs={12} sm={6}>
                                    <FormTextField<IFormValues>
                                        name="bookingOwner.invoiceCode"
                                        label={t('label.invoiceCode')}
                                        disabled={true}
                                    />
                                </Grid>
                            }
                            {formContext.getValues('bookingOwner.publicAuthority') &&
                                <Grid item xs={12} sm={6}>
                                    <FormTextField<IFormValues>
                                        name="bookingOwner.paIdNumber"
                                        label={t('label.paIdNumber')}
                                        disabled={true}
                                    />
                                </Grid>
                            }
                            <Grid item xs={6} sm={3}>
                                <FormTextField<IFormValues>
                                    name="bookingOwner.vatNumberCountry"
                                    label={t('label.vatNumberCountry')}
                                    disabled={true}
                                />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <FormTextField<IFormValues>
                                    name="bookingOwner.vatNumber"
                                    label={t('label.vatNumber')}
                                    disabled={true}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                    name="bookingOwner.fiscalCode"
                                    label={t('label.fiscalCode')}
                                    disabled={true}
                                />
                            </Grid>
                        </>
                        }
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="bookingOwner.email"
                                label={t('label.email')}
                                disabled={true}
                            />
                        </Grid>
                        <Grid container item xs={12} sm={6} columnSpacing={1}>
                            <Grid item xs={6} sm={6}>
                                <MobilePrefixField />
                            </Grid>
                            <Grid item xs={6} sm={6}>
                                <FormTextField<IFormValues>
                                    name="bookingOwner.mobile"
                                    label={t('label.mobile')}
                                    disabled
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </>
    );
};

export default BookingOwnerDetails;
